import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { AlertTriangle, Inbox, Lock, RotateCcw, type LucideIcon } from 'lucide-react';
import { describeError, isForbidden } from '../api/errors';

export function EmptyState({ icon: Icon = Inbox, title, description, action }: { icon?: LucideIcon; title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <Box role="status" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, px: 3, py: { xs: 4, sm: 6 }, color: 'text.secondary' }}>
      <Box aria-hidden sx={{ display: 'grid', placeItems: 'center', width: 56, height: 56, borderRadius: '14px', backgroundColor: 'var(--color-primary-soft)', color: 'primary.main' }}>
        <Icon size={26} strokeWidth={1.75} />
      </Box>
      <Typography variant="h6" component="p" sx={{ color: 'text.primary' }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" component="div" sx={{ maxWidth: 460 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
}

export function FehlerZustand({ title = 'Daten konnten nicht geladen werden', description, onRetry, retryLabel = 'Erneut versuchen' }: { title?: string; description?: ReactNode; onRetry?: () => void; retryLabel?: string }) {
  return (
    <Box role="alert" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, px: 3, py: { xs: 4, sm: 6 }, color: 'text.secondary' }}>
      <Box aria-hidden sx={{ display: 'grid', placeItems: 'center', width: 56, height: 56, borderRadius: '14px', backgroundColor: 'var(--color-danger-soft)', color: 'var(--color-danger)' }}>
        <AlertTriangle size={26} strokeWidth={1.75} />
      </Box>
      <Typography variant="h6" component="p" sx={{ color: 'text.primary' }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" component="div" sx={{ maxWidth: 460 }}>
          {description}
        </Typography>
      )}
      {onRetry && (
        <Button variant="outlined" startIcon={<RotateCcw size={16} />} onClick={onRetry} sx={{ mt: 1 }}>
          {retryLabel}
        </Button>
      )}
    </Box>
  );
}

export function ForbiddenState({ what = 'diesen Bereich' }: { what?: string }) {
  return (
    <EmptyState
      icon={Lock}
      title="Keine Berechtigung"
      description={`Sie haben keinen Zugriff auf ${what}. Wenden Sie sich an einen Autopilot-Admin, wenn Sie Zugriff benötigen.`}
    />
  );
}

export function LoadingState({ label = 'Wird geladen …', rows = 0 }: { label?: string; rows?: number }) {
  if (rows > 0) {
    return (
      <Box role="status" aria-label={label} sx={{ p: 2 }}>
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} height={36} sx={{ my: 0.5 }} />
        ))}
      </Box>
    );
  }
  return (
    <Box role="status" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6, color: 'text.secondary' }}>
      <CircularProgress size={22} />
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
}

/** Einheitliche Loading/Forbidden/Retry/Fehlerbehandlung für Queries. */
export function QueryBoundary({
  query,
  action,
  what,
  children,
  loadingRows,
}: {
  query: { isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  action: string;
  what?: string;
  children: ReactNode;
  loadingRows?: number;
}) {
  if (query.isPending) return <LoadingState rows={loadingRows} />;
  if (query.isError) {
    if (isForbidden(query.error)) return <ForbiddenState what={what} />;
    const d = describeError(query.error, action);
    return <FehlerZustand title={d.title} description={`${d.message} ${d.next}`} onRetry={() => void query.refetch()} />;
  }
  return <>{children}</>;
}

/** Fehlerhinweis für Aktionen (Mutationen): Aktion + nächster Schritt, mit Icon. */
export function ErrorAlert({ error, action, onClose }: { error: unknown; action: string; onClose?: () => void }) {
  if (!error) return null;
  const d = describeError(error, action);
  return (
    <Alert severity="error" icon={<AlertTriangle size={20} />} onClose={onClose} role="alert">
      <AlertTitle sx={{ fontWeight: 700 }}>{d.title}</AlertTitle>
      {d.message} {d.next}
      {d.correlationId && (
        <Typography variant="caption" component="div" sx={{ mt: 0.5, color: 'text.secondary' }}>
          Referenz für den Support: {d.correlationId}
        </Typography>
      )}
    </Alert>
  );
}
